import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button, Progress, cn } from '@parrot-software/design-system';
import { ArrowLeft, LogOut } from '@parrot-software/design-system/icons';

import { getUserCurrentStore } from 'utils/authTyped';

type WizardHeaderProps = {
  currentStep?: number;
  isFirstStep?: boolean;
  isSaving?: boolean;
  onBack?: () => void;
  totalSteps?: number;
};

/**
 * Unified wizard header — lives inside the Card across all breakpoints.
 * Nav row: back (optional) · store name · save & exit.
 * Progress row: rendered only when both currentStep and totalSteps are provided.
 */
export const WizardHeader = ({ currentStep, isFirstStep, isSaving, onBack, totalSteps }: WizardHeaderProps) => {
  const { t } = useTranslation('onboarding');
  const history = useHistory();
  const storeName = getUserCurrentStore()?.name ?? '';
  const showBack = !!onBack;
  const showProgress = typeof currentStep === 'number' && typeof totalSteps === 'number';
  const progressPercentage = showProgress ? ((currentStep + 1) / totalSteps) * 100 : 0;

  const handleSaveAndExit = () => {
    history.push('/signout');
  };

  return (
    <div className="flex flex-col gap-2 lg:hidden">
      <div className="flex h-9 items-center gap-2">
        {showBack && (
          <Button
            aria-label={t('wizard.back')}
            className={cn('size-9 shrink-0 p-2', { invisible: isFirstStep })}
            disabled={isSaving || isFirstStep}
            onClick={onBack}
            variant="outline"
          >
            <ArrowLeft size={16} />
          </Button>
        )}
        <p className="flex-1 truncate text-center text-xs font-normal leading-3 text-foreground">{storeName}</p>
        <Button
          aria-label={t('wizard.saveAndExit')}
          className="size-9 shrink-0 p-2 text-foreground"
          disabled={isSaving}
          onClick={handleSaveAndExit}
          variant="ghost"
        >
          <LogOut size={16} />
        </Button>
      </div>
      {showProgress && (
        <div className="flex flex-col gap-2">
          <p className="text-xs font-medium leading-3 text-muted-foreground">
            {t('wizard.progress', { current: currentStep + 1, total: totalSteps })}
          </p>
          <Progress className="h-1 [&>div]:bg-primary" value={progressPercentage} />
        </div>
      )}
    </div>
  );
};
